<!-- ============================ ACCOUNT CHOICE ============================
     THE ESCAPE HATCH FROM THE DEGRADED STATE.

     When this browser holds more than one signed-in account and none is pinned,
     startup deliberately stops rather than guessing an identity, because
     guessing loads the wrong user's profile and the wrong encryption key. That
     refusal is correct. Rendering it as a spinner was not: the app looked hung,
     it never resolved, and the only way out was DevTools. This says what is
     wrong and gives the user the one action that fixes it.

     Deliberately checked BEFORE isLoading, so it replaces the spinner instead
     of hiding behind it. It does not depend on the automatic prompt having
     fired, so it still works if that stood down for this page load. -->
<div class="loadingDiv" *ngIf="activeAccount.isAmbiguous()">
  <img src="/assets/images/Forensic_Notes_Logo.png" class="loadingLogo" alt="Forensic Notes" />
  <div style="max-width:34rem;text-align:center;padding:1.5rem;margin:0 auto;">
    <h2 style="margin:0 0 .75rem;font-size:1.25rem;">Choose an account to continue</h2>
    <p style="margin:0 0 1.25rem;line-height:1.5;">
      More than one Forensic Notes account is signed in on this browser. To make sure
      your notes and your encryption key belong to the right account, please choose
      which one to use.
    </p>
    <button type="button" (click)="activeAccount.promptForAccount()"
      style="padding:.6rem 1.25rem;font-size:1rem;cursor:pointer;">
      Choose account
    </button>
  </div>
</div>

<!-- Loading GIF Panel -->
<div #loadingDiv class="loadingDiv" *ngIf="isLoading && !activeAccount.isAmbiguous()">
  <img src="/assets/images/Forensic_Notes_Logo.png" class="loadingLogo" tooltip="Loading Forensic Notes" />
  <img src="/assets/images/loading-gif-png-5.gif" class="loadingAppGif" tooltip="Loading Forensic Notes" />
</div>

<!-- Main App Display -->
<mat-toolbar class="navbar">
  <ng-container *ngIf="!(notShowLayout$ | async)">
    <button class="bar-btn bar-btn--menu" type="button" *ngIf="sidenav.mode === 'over'" (click)="sidenav.toggle()"
      [attr.aria-label]="sidenav.opened ? 'Close menu' : 'Open menu'">
      <mat-icon>{{ sidenav.opened ? 'close' : 'menu' }}</mat-icon>
    </button>
  </ng-container>

  <div id="service-worker-events"></div>
  <div class="navContainer">
    <div class="logoContainer">
      <img src="assets/images/logo.png" alt="Forensic Notes" />
    </div>
    <div class="rightItems">

      <button class="bar-btn" type="button" (click)="themeService.cycle()"
        [title]="themeService.label" [attr.aria-label]="themeService.label">
        <mat-icon>{{ themeService.icon }}</mat-icon>
      </button>

      <button class="bar-btn" type="button" (click)="onShowNotification($event)"
        title="Notifications" aria-label="Notifications">
        <mat-icon>notifications</mat-icon>
        <span class="bar-badge" *ngIf="nCount > 0">{{ nCount > 99 ? '99+' : nCount }}</span>
      </button>

      <button class="bar-btn" type="button" (click)="showMenu()" title="Options" aria-label="Options">
        <mat-icon>more_vert</mat-icon>
      </button>

    </div>
  </div>
</mat-toolbar>

<mat-sidenav-container [class.dialogDrag-section]="!(notShowLayout$ | async)">
  <mat-sidenav #sidenav="matSidenav" [opened]="!(notShowLayout$ | async) && sidenav.opened">
    <!-- Material puts projected content inside its own .mat-drawer-inner-container
         (no _ngcontent attr), so the column-flex that pins .snav-foot to the bottom
         has to live on a wrapper of our own. -->
    <div class="snav-shell">
    <nav class="snav" aria-label="Main navigation">
      <div class="snav-label">Workspace</div>
      <button class="snav-item" type="button" [routerLink]="['/']" routerLinkActive="is-active"
        [routerLinkActiveOptions]="{exact: true}" (click)="closeSideMenuIfMobile()">
        <mat-icon>home</mat-icon>
        <span class="snav-txt">Home</span>
      </button>
      <button class="snav-item" type="button" [routerLink]="['/notebooks/my-notebooks']" routerLinkActive="is-active"
        (click)="closeSideMenuIfMobile()">
        <mat-icon>person</mat-icon>
        <span class="snav-txt">My Notebooks</span>
      </button>
      <!-- Show Team Notebooks Only for Group Admin -->
      <button class="snav-item" type="button" [routerLink]="['/notebooks/team-notebooks']" routerLinkActive="is-active"
        *ngIf="showTeamNotebooksMenu" (click)="closeSideMenuIfMobile()">
        <mat-icon>groups</mat-icon>
        <span class="snav-txt">Team Notebooks</span>
      </button>
      <!-- Cases. ALWAYS shown, on Rob's instruction 2026-09-15 — there is no longer
           a flag that can hide it. It used to be gated on showCMS, which the app
           then force-set to false at runtime regardless of the environment file,
           so the entry was unreachable for everyone on every door.

           The label is the literal "Cases". It used to be
           `{{customProjectNaming.masterName}}s`, which renders "Projects" or
           "Cases" depending on a per-account naming level and, before that call
           returns, an empty string — which is why the nav could show "..." as a
           menu item. A fixed name cannot do that.

           Routes to /cases, the flat Cases console on the Notes API. It used to
           point at /cms/, which loads the retired CRM settings and never
           resolves: "Failed to Load CMS Settings", spinning forever. -->
      <button class="snav-item" type="button" [routerLink]="['/cases']" routerLinkActive="is-active"
        (click)="closeSideMenuIfMobile()">
        <mat-icon>folder</mat-icon>
        <span class="snav-txt">Cases</span>
      </button>
      <button class="snav-item" type="button" [routerLink]="['/entities']" routerLinkActive="is-active"
        (click)="closeSideMenuIfMobile()">
        <mat-icon>fingerprint</mat-icon>
        <span class="snav-txt">Entities</span>
      </button>
      <!-- CMS discontinued 2026-07: Work Requests, Incident Numbers, Contacts and Assets
           are part of the retired Case Management (CRM) suite (backed by the dead
           crm-api). All hidden from nav. -->

      <div class="snav-label">Support</div>
      <button class="snav-item" type="button"
        onClick="window.open('https://www.forensicnotes.com/support','_blank','noopener,noreferrer')">
        <mat-icon>quiz</mat-icon>
        <span class="snav-txt">Help</span>
        <mat-icon class="snav-ext">open_in_new</mat-icon>
      </button>
      <button class="snav-item" type="button" [routerLink]="['/report-a-bug']" routerLinkActive="is-active"
        (click)="closeSideMenuIfMobile()">
        <mat-icon>bug_report</mat-icon>
        <span class="snav-txt">Report a Bug</span>
      </button>

      <!-- Tools group: only rendered when it actually holds something, so
           hiding AI Tools doesn't leave an orphan label above nothing. -->
      <div class="snav-label" *ngIf="showAiToolsMenu || securityUtils.isServiceWorkerCacheEnabled()">Tools</div>
<ng-container *fnIfAi="'ReportAnalysis'">
      <button class="snav-item" type="button" *ngIf="showAiToolsMenu" [routerLink]="['/ai-tools']"
        routerLinkActive="is-active" (click)="closeSideMenuIfMobile()">
        <img class="snav-png" src="/assets/images/chatgpt-white-88.png" alt="" />
        <span class="snav-txt">AI Tools</span>
      </button>
</ng-container>
      <button class="snav-item" type="button" *ngIf="securityUtils.isServiceWorkerCacheEnabled()"
        [routerLink]="['/clientSideCaching']" routerLinkActive="is-active" (click)="closeSideMenuIfMobile()">
        <mat-icon>cloud_done</mat-icon>
        <span class="snav-txt">Cache Settings</span>
      </button>
    </nav>

    <div class="snav-foot">
      <button class="snav-promo" type="button" *ngIf="securityUtils.isServiceWorkerCacheEnabled() == false"
        [routerLink]="['/clientSideCaching']" (click)="closeSideMenuIfMobile()">
        <span class="promo-ico"><mat-icon>offline_bolt</mat-icon></span>
        <span class="promo-tx">
          <span class="promo-t">Enable offline mode</span>
          <span class="promo-s">Cache notebooks on this device for offline access &amp; faster loads</span>
        </span>
        <mat-icon class="promo-arrow">chevron_right</mat-icon>
      </button>

      <button class="snav-warn" type="button" *ngIf="isTheSameTimeZoneOffset == false"
        (click)="CheckProfileAndSetup(true)" title="Click to re-check">
        <span class="warn-head"><mat-icon>schedule</mat-icon>Timezone mismatch</span>
        <span class="warn-tx">
          Your device's timezone differs from your profile setting. Note date/times can't
          be adjusted until this is corrected. Contact support for help.
        </span>
        <span class="warn-cta">Tap to re-check</span>
      </button>
    </div>
    </div>
  </mat-sidenav>
  <!-- MAIN CONTENT -->
  <mat-sidenav-content>
    <div>
      <router-outlet></router-outlet>
      <div class="offlineOverlay" *ngIf="!(isOnline$ | async)">
        <mat-icon [inline]="true" class="offlineIcon">cloud_off</mat-icon>Network Offline
      </div>
    </div>
  </mat-sidenav-content>
  <!-- (end of) MAIN CONTENT -->
</mat-sidenav-container>

<!-- Menu Dialog -->
<ejs-dialog id="ejDialogheadMenu" #ejDialogheadMenu header="Options" showCloseIcon='true' cssClass="fn-menu-dialog"
  (overlayClick)="onOverlayClickheadMenu()" width="300px" [visible]="false" [animationSettings]='animationSettings'
  [target]="dialogDragTarget" [allowDragging]='allowDialogDragging' [enableResize]='false' isModal="true">
  <ng-template #content>
    <button mat-button class="mat-icon-menu-button" (click)="goSetting(settingApp)">
      <mat-icon>settings</mat-icon>
      Profile & Settings
    </button>
    <hr>
    <button mat-button class="mat-icon-menu-button" (click)="updateApp()">
      <mat-icon>system_update</mat-icon>
      Get Latest Version
    </button>
    <button mat-button class="mat-icon-menu-button" (click)="logout()">
      <mat-icon>exit_to_app</mat-icon>
      Logout
    </button>
    <p class="menuFootnote center">
      Version: {{curentVersion$ | async}}
    </p>

  </ng-template>
</ejs-dialog>

<!-- Notification Dialog -->
<ejs-dialog id="dialogNotification" header="Notifications" showCloseIcon="true" #ejDialogNotification
  cssClass="fn-dialog fn-notify-dialog" (overlayClick)="onOverlayClickNotification()" width="440px" [visible]="false"
  class="notifications" [position]='topPosition' [animationSettings]='animationSettings' [target]="dialogDragTarget"
  [allowDragging]='allowDialogDragging' [enableResize]='false' isModal="true">
  <div id="Allnotification">
    <div class="notif-toolbar">
      <a class="notif-chip" (click)="unreadNotification()"><i class="fas fa-filter"></i>Unread only</a>
      <a class="notif-chip" (click)="readAllNotification()"><i class="fas fa-check-double"></i>Mark all as read</a>
      <a class="notif-chip notif-chip--icon" id="Refresh" title="Refresh"
        (click)="refreshnotification(fullcount)"><i class="fas fa-sync"></i></a>
    </div>
    <div class="main-n-list">
      <div #pageContent class="notif-item"
        *ngFor="let notification of (allNotification | paginate: { itemsPerPage: 3, currentPage: p })">
        <!-- UNRead -->
        <div class="notif-row" (click)="markAsReadOrUnread(notification)" title="Click to Mark as Read"
          *ngIf="notification.dateTimeNotificationRead === undefined" id="ntf_{{ notification.notificationID }}">
          <span class="notif-ico"><mat-icon>notifications_active</mat-icon></span>
          <span class="notif-body">
            <span class="notif-title">{{ notification.title }}<span class="notif-dot"></span></span>
            <span [innerHTML]="notification.notificationMessage" class="notif-msg"></span>
            <span class="notif-when">Created {{notification.dateNotificationCreatedStr}} UTC</span>
          </span>
        </div>
        <!-- READ -->
        <div class="notif-row is-read" (click)="markAsReadOrUnread(notification)"
          *ngIf="notification.dateTimeNotificationRead !== undefined" id="ntf_{{ notification.notificationID }}">
          <span class="notif-ico"><mat-icon>notifications</mat-icon></span>
          <span class="notif-body">
            <span class="notif-title">{{ notification.title }}</span>
            <span [innerHTML]="notification.notificationMessage" class="notif-msg"></span>
            <span class="notif-when">Created {{notification.dateNotificationCreatedStr}} UTC</span>
            <span class="notif-when">Read {{notification.dateTimeNotificationReadStr}} UTC</span>
          </span>
        </div>
      </div>
    </div>
    <pagination-controls class="notif-pager" (pageChange)="pageChanged($event)"
      (pageBoundsCorrection)="pageChanged($event)" maxSize="5" previousLabel="Prev." nextLabel="Next">
    </pagination-controls>
  </div>
  <div id="ViewAllNotification" class="view-all-notification-group">
    <span class="notif-empty-ico"><mat-icon>notifications_none</mat-icon></span>
    <p class="notif-empty-h">You're all caught up</p>
    <p class="notif-empty-s">No unread notifications. <a (click)="viewAllNotification()" class="notification-text">View
        all</a> to see your full history.</p>
  </div>
</ejs-dialog>


<app-download-confirmation-dialog #ejDialogConfirmDownload></app-download-confirmation-dialog>

<app-setting #ejDialogSettings></app-setting>

<app-account-setup #componentAccountSetup></app-account-setup>

<app-encryption-password #componentEncrytionPassword></app-encryption-password>

<ng-container #dynamicDialogComponents></ng-container>
